import { cn } from '@/lib/utils';

export interface Fact {
  label: string;
  /** Null or empty means "we do not have this", and the row is dropped. */
  value: string | null | undefined;
  /** Renders in the foreground colour rather than muted. */
  emphasis?: boolean;
  tone?: 'positive' | 'negative';
}

export interface FactGridProps {
  facts: Array<Fact>;
  className?: string;
}

/**
 * A label/value grid for the reader pane.
 *
 * EMPTY FACTS ARE DROPPED, not rendered as "—". A dash is a claim that the
 * field exists and is blank, which is right in a table where the columns are
 * fixed and the reader is comparing rows. Here there is one record and no
 * comparison, so twelve labels with eight dashes under them reads as a broken
 * fetch. Whatever we know, we show; what we do not know is not mentioned.
 *
 * This is also why the grid is `auto-rows` rather than a fixed twelve cells:
 * the shape of the block is the shape of what is actually known about this
 * record, and a sparse client simply gets a shorter block.
 *
 * `dl` and not a table: these are name/value pairs about ONE subject, which is
 * exactly the definition list's job. A table would promise columns that mean
 * something across rows, and here the rows are unrelated to each other.
 */
export const FactGrid = ({ facts, className }: FactGridProps) => {
  const known = facts.filter((f) => f.value != null && f.value !== '');
  if (known.length === 0) return null;

  return (
    <dl
      className={cn(
        'grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3',
        className,
      )}
    >
      {known.map((fact) => (
        <div key={fact.label} className="min-w-0">
          <dt className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
            {fact.label}
          </dt>
          <dd
            className={cn(
              // `break-words`: an email or a reference code has no spaces and
              // would otherwise push the grid wider than the dialog.
              'mt-1 text-sm break-words',
              fact.tone === 'positive' &&
                'text-emerald-600 dark:text-emerald-400',
              fact.tone === 'negative' && 'text-red-600 dark:text-red-400',
              !fact.tone && (fact.emphasis ? 'font-medium' : 'text-foreground'),
            )}
          >
            {fact.value}
          </dd>
        </div>
      ))}
    </dl>
  );
};
